import { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, Mail, MessageSquare, Building2, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "Question générale", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setLoading(true);
    // Mocked - in production, hit /api/contact
    setTimeout(() => {
      setSent(true); setLoading(false);
      toast.success("Message reçu. Nous vous répondons sous 4 heures.");
    }, 700);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-4 z-50 px-4">
        <div className="max-w-4xl mx-auto glass-strong rounded-full border border-border h-14 flex items-center justify-between px-4">
          <Link to="/"><Logo size={20} /></Link>
          <ThemeToggle />
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-16 lg:py-24">
        <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />Retour
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary">Support</p>
            <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tighter mt-3">
              Une question ? <span className="font-serif-italic font-normal">Parlons-en.</span>
            </h1>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              L'équipe Local Pulse est basée à Paris. Nous répondons en moins de 4 heures ouvrées, en français.
            </p>
            <div className="mt-10 space-y-5">
              {[
                { icon: Mail, t: "Email direct", v: "support@getlocalpulse.fr" },
                { icon: MessageSquare, t: "Chat en direct", v: "Lundi-vendredi · 9h-19h" },
                { icon: Building2, t: "Adresse", v: "32 rue du Faubourg Saint-Denis · 75010 Paris" },
              ].map(c => (
                <div key={c.t} className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary/15 to-violet-500/15 flex items-center justify-center ring-1 ring-border">
                    <c.icon className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{c.t}</p>
                    <p className="text-sm font-medium mt-0.5">{c.v}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-3">
            {sent ? (
              <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-2xl p-12 text-center" data-testid="contact-success">
                <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/15 flex items-center justify-center">
                  <Send className="h-6 w-6 text-emerald-500" />
                </div>
                <h2 className="mt-5 text-2xl font-black font-display tracking-tighter">Message envoyé</h2>
                <p className="text-muted-foreground mt-2">Vous recevrez une réponse à <span className="font-semibold text-foreground">{form.email}</span> sous 4 heures.</p>
                <Link to="/"><Button className="mt-6 rounded-full">Retour à l'accueil</Button></Link>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-border rounded-2xl p-7 bg-card space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider font-bold">Nom</Label>
                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-md h-11" data-testid="contact-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider font-bold">Email</Label>
                    <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-md h-11" data-testid="contact-email" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold">Sujet</Label>
                  <select value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    className="w-full rounded-md h-11 border border-border bg-background px-3 text-sm" data-testid="contact-topic">
                    <option>Question générale</option>
                    <option>Problème technique</option>
                    <option>Démo / partenariat</option>
                    <option>Facturation</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider font-bold">Message</Label>
                  <Textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-md" placeholder="Comment pouvons-nous vous aider ?" data-testid="contact-message" />
                </div>
                <Button type="submit" disabled={loading} className="w-full rounded-full h-11 btn-glow" data-testid="contact-submit">
                  <Send className="h-4 w-4 mr-2" />{loading ? "Envoi…" : "Envoyer"}
                </Button>
                <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1.5">
                  <ShieldCheck className="h-3 w-3" />Vos messages sont chiffrés · RGPD
                </p>
              </form>
            )}
          </div>
        </div>
      </main>
      <footer className="border-t border-border py-8 px-6 text-center text-xs text-muted-foreground">
        © 2026 Local Pulse · <Link to="/privacy" className="hover:text-foreground">Confidentialité</Link> · <Link to="/terms" className="hover:text-foreground">CGU</Link>
      </footer>
    </div>
  );
}
